import {
	Injectable,
	Signal,
	WritableSignal,
	computed,
	effect,
	signal,
	untracked,
} from '@angular/core';
import { DEFAULT_THEME, isBuiltInThemeId } from '../defaults/theme.defaults';
import { ThemeConfig, ThemeModeColors } from '../models/theme.model';
import { applyElementThemeVariables } from '../utils/apply-element-theme-variables.util';
import {
	isSameLoadedThemeConfig,
	normalizeThemeConfig,
} from '../utils/normalize-theme-typography.util';

/** Theme mode options for light/dark appearance */
export type ThemeMode = 'light' | 'dark' | 'auto';

/**
 * Theme Service
 *
 * Manages application themes and appearance modes. This service handles:
 * 1. Light/dark mode preferences and switching
 * 2. Theme configuration management
 * 3. CSS variable application
 *
 * The service uses Angular signals for reactive state management.
 */
@Injectable({
	providedIn: 'root',
})
export class ThemeService {
	// Flag to track if user has explicitly overridden the layout's theme
	private userHasOverriddenTheme = false;

	// Stores the layout's configured theme mode (auto/light/dark)
	private layoutConfiguredThemeMode: ThemeMode = 'auto';

	// Flag to defer CSS application until theme is explicitly initialized
	private themeInitialized = false;

	//
	// THEME MODE SIGNALS (light/dark preference)
	//

	/**
	 * Theme mode preference signal - reflects what the switcher shows
	 * This matches the layout config until the user explicitly changes it
	 */
	private themeModePreferenceSignal: WritableSignal<ThemeMode> =
		signal('auto');

	/**
	 * Active theme mode signal (always resolves to either 'light' or 'dark')
	 * This is the actual mode in use, resolving 'auto' to the system preference
	 */
	private activeThemeModeSignal: WritableSignal<'light' | 'dark'> =
		signal('light');

	/**
	 * Get the active theme mode signal (light or dark)
	 * @returns Signal with the active theme mode
	 */
	public getActiveThemeModeSignal(): Signal<'light' | 'dark'> {
		return this.activeThemeModeSignal;
	}

	/**
	 * Get the user's theme mode preference signal
	 * @returns Signal with the user's theme mode preference
	 */
	public getThemeModePreferenceSignal(): Signal<ThemeMode> {
		return this.themeModePreferenceSignal;
	}

	/**
	 * Signal that holds the complete theme configuration
	 */
	private themeConfigSignal = signal<ThemeConfig | null>(null);

	/**
	 * Get the current theme configuration
	 * @returns The current theme config or DEFAULT_THEME if none is set
	 */
	public getThemeConfig(): ThemeConfig {
		return this.themeConfigSignal() || DEFAULT_THEME;
	}

	/** Explicitly loaded theme config, or null before the first apply. */
	public getAppliedThemeConfig(): ThemeConfig | null {
		return this.themeConfigSignal();
	}

	/**
	 * Signal for the current theme ID (for debug display and reactive bindings).
	 * Updates when the applied theme config changes (e.g. after selectThemeById).
	 */
	public readonly themeIdDisplaySignal = computed(
		() => this.getThemeConfig().themeId ?? '—'
	);

	/**
	 * Signal that stores all available themes
	 */
	private availableThemesSignal = signal<ThemeConfig[]>([]);

	/**
	 * Signal for the currently selected theme ID
	 */
	private selectedThemeIdSignal = signal<string | null>(null);

	/**
	 * Computed signal that provides the currently selected theme object
	 */
	public selectedThemeSignal = computed(() => {
		const themeId = this.selectedThemeIdSignal();
		const themes = this.availableThemesSignal();

		if (!themeId || themes.length === 0) return null;

		return themes.find(theme => theme.themeId === themeId) || null;
	});

	constructor() {
		// Initialize theme signals (but don't apply CSS yet)
		this.initTheme();

		// Listen for system theme changes
		this.listenForSystemThemeChanges();

		// Effect to apply CSS variables when theme changes
		// CSS is only applied after theme is explicitly initialized
		effect(() => {
			const currentThemeMode = this.activeThemeModeSignal();
			const themeConfig = this.getThemeConfig();

			// Only apply CSS after theme has been explicitly initialized
			// This prevents flash when ThemeService is created before layout sets theme
			if (this.themeInitialized) {
				this.applyCssVariables(
					themeConfig,
					currentThemeMode,
					'pal_jepto'
				);
			}
		});

		// Sync themeConfigSignal when the selected theme changes. Reads of the
		// active config are untracked so loading a fuller config for the same
		// theme (e.g. getTheme after listThemes) cannot re-trigger this effect
		// and clobber it with the older selected row.
		effect(() => {
			const selected = this.selectedThemeSignal();
			if (!selected) {
				return;
			}
			untracked(() => {
				const active = this.themeConfigSignal();
				if (active?.themeId === selected.themeId) {
					return;
				}
				this.themeConfigSignal.set(normalizeThemeConfig(selected));
			});
		});
	}

	/**
	 * Load theme configuration from config object
	 * @param themeConfig The theme configuration to load
	 */
	public loadThemeFromConfig(themeConfig: ThemeConfig): void {
		untracked(() => {
			const normalized = normalizeThemeConfig(themeConfig);
			if (isSameLoadedThemeConfig(this.themeConfigSignal(), normalized)) {
				this.applyCssVariablesNow();
				return;
			}
			this.themeConfigSignal.set(normalized);
			this.themeInitialized = true;
			this.applyCssVariablesNow();
		});
	}

	/**
	 * Bootstrap `:root` CSS from a layout themeConfig when none is active yet;
	 * otherwise re-apply the active theme only (never downgrade from a stale input).
	 */
	public syncReportThemeCss(themeConfig: ThemeConfig): void {
		untracked(() => {
			if (!this.themeConfigSignal()) {
				this.loadThemeFromConfig(themeConfig);
				return;
			}
			this.applyCssVariablesNow();
		});
	}

	/**
	 * Set the theme mode (light, dark, or auto)
	 * Called when user clicks on the theme switcher
	 * @param mode The theme mode to set
	 */
	public setThemeMode(mode: ThemeMode): void {
		// Mark that user has explicitly overridden the layout's theme
		this.userHasOverriddenTheme = true;
		// Mark theme as initialized so CSS will be applied
		this.themeInitialized = true;
		this.themeModePreferenceSignal.set(mode);
		this.applyThemeMode(mode);
		// Manually apply CSS since signal might not trigger effect if value unchanged
		this.applyCssVariablesNow();
	}

	/**
	 * Clears the in-session "user changed theme" latch so the next
	 * {@link initFromViewConfig} applies (for example after loading a different report).
	 */
	public prepareForViewConfigInit(): void {
		this.userHasOverriddenTheme = false;
	}

	/**
	 * Initialize theme from layout configuration.
	 * Sets the switcher to match the layout's theme setting.
	 * @param mode The theme mode from the layout configuration (auto/light/dark)
	 */
	public initFromViewConfig(mode: ThemeMode): void {
		// Store the layout's configured theme mode
		this.layoutConfiguredThemeMode = mode;

		// If user has explicitly overridden, don't change anything
		if (this.userHasOverriddenTheme) {
			return;
		}

		// Mark theme as initialized so CSS will be applied
		this.themeInitialized = true;
		// Set the preference signal to match the layout config (what the switcher shows)
		this.themeModePreferenceSignal.set(mode);
		// Apply the theme
		this.applyThemeMode(mode);
		// Manually apply CSS to ensure immediate application
		this.applyCssVariablesNow();
	}

	/**
	 * Immediately apply CSS variables (bypasses effect scheduling)
	 */
	private applyCssVariablesNow(): void {
		const currentThemeMode = this.activeThemeModeSignal();
		const themeConfig = this.getThemeConfig();
		this.applyCssVariables(themeConfig, currentThemeMode, 'pal_jepto');
	}

	/**
	 * Apply a theme mode - resolves 'auto' to system preference
	 */
	private applyThemeMode(mode: ThemeMode): void {
		if (mode === 'auto') {
			// Auto uses system preference
			const isDarkMode = window.matchMedia(
				'(prefers-color-scheme: dark)'
			).matches;
			this.setActiveThemeMode(isDarkMode ? 'dark' : 'light');
		} else if (mode === 'light' || mode === 'dark') {
			this.setActiveThemeMode(mode);
		} else {
			this.setActiveThemeMode('light');
		}
	}

	/**
	 * Set active theme mode without changing user preference
	 * Used internally when 'auto' is the preference
	 */
	private setActiveThemeMode(mode: 'light' | 'dark'): void {
		this.activeThemeModeSignal.set(mode);
	}

	/**
	 * Update available themes collection
	 * @param themes Array of theme objects
	 */
	public setAvailableThemes(themes: ThemeConfig[]): void {
		if (!themes || themes.length === 0) return;

		this.availableThemesSignal.set(themes);

		// If no theme is selected yet, select the first one
		if (!this.selectedThemeIdSignal() && themes.length > 0) {
			this.selectThemeById(themes[0].themeId);
		}
	}

	/**
	 * Adds or replaces themes in the available list without changing the active
	 * selection (safe for report settings option hydration).
	 *
	 * Intentional theme preview/selection still updates the canvas via
	 * {@link loadThemeFromConfig} before catalog rows are merged here.
	 */
	public mergeAvailableThemes(themes: ThemeConfig[]): void {
		if (!themes?.length) {
			return;
		}

		const prior = this.availableThemesSignal();
		const merged = new Map(prior.map(theme => [theme.themeId, theme]));
		const activeTheme = this.themeConfigSignal();
		for (const theme of themes) {
			const themeId = theme.themeId?.trim();
			if (!themeId) {
				continue;
			}
			// Catalog rows from listThemes must not downgrade the active canvas theme.
			if (activeTheme?.themeId === themeId) {
				merged.set(themeId, normalizeThemeConfig(activeTheme));
				continue;
			}
			merged.set(themeId, normalizeThemeConfig(theme));
		}
		const next = [...merged.values()];
		if (
			prior.length === next.length &&
			prior.every(
				(theme, index) =>
					theme.themeId === next[index]?.themeId &&
					isSameLoadedThemeConfig(theme, next[index]!)
			)
		) {
			return;
		}
		this.availableThemesSignal.set(next);
	}

	/**
	 * Get a theme by ID from available themes, or the built-in default when themeId is the built-in ID.
	 * @param themeId Theme ID (e.g. 'theme_jepto')
	 * @returns Theme config or null if not found
	 */
	public getThemeById(themeId: string): ThemeConfig | null {
		const active = this.themeConfigSignal();
		if (active?.themeId === themeId) {
			return active;
		}
		const themes = this.availableThemesSignal();
		const found = themes.find(t => t.themeId === themeId);
		if (found) {
			return found;
		}
		if (isBuiltInThemeId(themeId)) return DEFAULT_THEME;
		return null;
	}

	/**
	 * Select a theme by its ID
	 * @param themeId ID of the theme to select
	 * @returns boolean indicating success
	 */
	public selectThemeById(themeId: string): boolean {
		const themes = this.availableThemesSignal();
		const themeExists = themes.some(theme => theme.themeId === themeId);

		if (!themeExists) return false;

		this.selectedThemeIdSignal.set(themeId);

		return true;
	}

	// Private helper methods

	/**
	 * Initialize theme with default 'auto' mode
	 * Standalone cards default to 'auto' which follows system preference
	 */
	private initTheme(): void {
		this.themeModePreferenceSignal.set('auto');
		this.applyThemeMode('auto');
	}

	/**
	 * Listen for system theme changes
	 * Updates the active theme when preference is 'auto'
	 */
	private listenForSystemThemeChanges(): void {
		if (window.matchMedia) {
			const mediaQuery = window.matchMedia(
				'(prefers-color-scheme: dark)'
			);
			const handleChange = () => {
				// Only update if current preference is 'auto'
				if (this.themeModePreferenceSignal() === 'auto') {
					this.applyThemeMode('auto');
				}
			};

			if (mediaQuery.addEventListener) {
				mediaQuery.addEventListener('change', handleChange);
			}
		}
	}

	private applyCssVariables(
		themeConfig: ThemeConfig,
		themeMode: 'light' | 'dark',
		palette: string = 'auto'
	): void {
		const root = document.documentElement;
		const defaultModeColors = DEFAULT_THEME.colors[themeMode];
		const rawModeColors =
			themeConfig.colors?.[themeMode] ?? defaultModeColors;
		const themeColor: ThemeModeColors = {
			...defaultModeColors,
			...rawModeColors,
			border: {
				...defaultModeColors.border,
				...rawModeColors.border,
			},
			card: {
				...defaultModeColors.card,
				...rawModeColors.card,
			},
			text: {
				...defaultModeColors.text,
				...rawModeColors.text,
			},
			status: {
				...defaultModeColors.status,
				...rawModeColors.status,
			},
		};

		applyElementThemeVariables({
			element: root,
			themeConfig,
			activeMode: themeMode,
			paletteColors: [],
		});

		// Report-wide layout variables
		root.style.setProperty('--report-max-width', themeConfig.maxWidth);
		root.style.setProperty('--report-padding', themeConfig.padding);

		// Set color variables from theme
		root.style.setProperty(
			'--report-background-color',
			themeColor.background
		);
		root.style.setProperty('--text-primary-color', themeColor.text.primary);
		root.style.setProperty(
			'--text-secondary-color',
			themeColor.text.secondary
		);
		root.style.setProperty('--text-link-color', themeColor.text.link);
		root.style.setProperty(
			'--background-primary-color',
			themeColor.card.background
		);
		// Set background-color for Highcharts tooltips (rendered outside component)
		root.style.setProperty(
			'--background-color',
			themeColor.card.background
		);
		root.style.setProperty(
			'--border-primary-color',
			themeColor.border.primary
		);
		root.style.setProperty(
			'--border-secondary-color',
			themeColor.border.secondary
		);
		root.style.setProperty(
			'--card-shadow-color',
			themeConfig.card?.shadow === false
				? 'transparent'
				: themeColor.card.shadow
		);
		root.style.setProperty(
			'--card-background-color',
			themeColor.card.background
		);

		// Set card layout variables
		if (themeConfig.card) {
			root.style.setProperty(
				'--card-border-radius',
				themeConfig.card.borderRadius
			);
			root.style.setProperty(
				'--card-border-width',
				themeConfig.card.borderWidth
			);
			root.style.setProperty('--card-spacing', themeConfig.card.spacing);
			root.style.setProperty('--card-padding', themeConfig.card.padding);
			root.style.setProperty(
				'--card-content-alignment',
				themeConfig.card.contentAlignment
			);

			// Typography weights
			root.style.setProperty(
				'--card-heading-weight',
				this.getFontWeight(
					themeConfig.card.headingWeight,
					themeConfig.typography
				)
			);
			root.style.setProperty(
				'--card-title-weight',
				this.getFontWeight(
					themeConfig.card.titleWeight,
					themeConfig.typography
				)
			);

			// Header layout
			root.style.setProperty(
				'--card-header-margin-bottom',
				themeConfig.card.headerMarginBottom || '4px'
			);
			root.style.setProperty(
				'--card-header-padding-bottom',
				themeConfig.card.headerPaddingBottom || '6px'
			);
			root.style.setProperty(
				'--card-title-margin-bottom',
				themeConfig.card.titleMarginBottom || '2px'
			);

			// Icon
			root.style.setProperty(
				'--card-icon-size',
				themeConfig.card.iconSize || '24px'
			);
			root.style.setProperty(
				'--card-icon-margin-right',
				themeConfig.card.iconMarginRight || '8px'
			);
		}

		// Set theme mode as a class on the root element for easier theming
		root.classList.remove('light-theme', 'dark-theme');
		root.classList.add(`${themeMode}-theme`);
	}

	/**
	 * Helper to resolve font weight from theme config
	 */
	private getFontWeight(weightName: string, typography: any): string {
		if (!weightName) return '400';
		if (typography && typography.weight && typography.weight[weightName]) {
			return typography.weight[weightName].toString();
		}
		return weightName; // Fallback to returning the string (e.g. 'bold') if not found in map
	}

	/**
	 * Apply palette to the current theme
	 * @param paletteId ID of the palette to apply
	 */
	public applyPalette(paletteId: string): void {
		// Method is called by PaletteService
		// We don't need implementation here as palette colors
		// are directly applied via CSS variables in PaletteService
	}
}
